import '@salesforce-ux/design-system/assets/styles/salesforce-lightning-design-system-offline.min.css';
import { subscribe } from 'pubsub-js';
import { LeftPanel, RightPanel } from './components';
import { getUserDetails } from './services/user-service';
import { getUrlPath, parseURLParams } from './helpers/url-utils';
import { publish, PUBSUB_TOPICS } from './state/pubsub';
import './styles/style.css';
import { MapModalController } from './controllers/map-modal.controller';
import { MenuController } from './controllers/menu-controller';
import { GaligeoCloudService } from './services/galigeo-cloud.service';
import { PortalAppContext } from './state/portal-appcontext';
import { AdminRightPanel } from './components/admin/module-admin/module-admin.component';
import { DeployService } from './services/deploy.service';
import { TranslationUtils } from './helpers/translation-utils';
import { RESTUtils, Message, Toast, Translate } from '@galigeo-store/ui-lib';
import { LoggerClient } from './services/logger.client';

import { initNotificationDisplay } from './services/notification-display.service';
import { ApiService } from '@galigeo-store/ui-components';

const components: any = {};

const run = async () => {
  console.log('Running...');

  const { url } = parseURLParams();
  PortalAppContext.setBackendUrl(url);
  PortalAppContext.setTrackerUrl();

  const currentUser = await getUserDetails();
  PortalAppContext.setCurrentUser(currentUser);

  await TranslationUtils.loadTranslation(currentUser.lang);
  subscribe(PUBSUB_TOPICS.LANGUAGE_CHANGED, async (msg, data: any) => {
    const lang = data.lang;
    PortalAppContext.getCurrentUser().lang = lang;
    await GaligeoCloudService.setLanguage({ lang });
    await TranslationUtils.loadTranslation(lang);

    if (window.location.hash.includes('admin')) {
      window.location.reload();
      return;
    }

    publish(PUBSUB_TOPICS.REFRESH_LEFT_PANEL);
    publish(PUBSUB_TOPICS.REFRESH_IFRAME);
    LoggerClient.log('language-changed', data.lang);
  });

  document.querySelector<HTMLDivElement>('#app')!.innerHTML = `
    <div class="slds-grid container"></div>
  `;

  const workflowApi = new ApiService({
    baseUrl: import.meta.env.VITE_WORKFLOW_API_URL,
    token: RESTUtils.getToken(),
  });

  try {
    await workflowApi.awake();
    initNotificationDisplay();
  } catch (error) {
    console.error('[portal] Workflow API not healthy, skipping WebSocket notifications:', error);
  }

  // Check license expiration status
  const licDays = currentUser.licenseExpirationDays;
  if (licDays != null) {
    if (licDays <= 0) {
      PortalAppContext.setLicenseExpired(true);
      Toast.error(Translate.get('lic_expired'));
    } else if (licDays < 10) {
      Toast.warning(
        Translate.get('lic_expiring').replace('DAYS', String(licDays))
      );
    }
  }

  const urlPath = getUrlPath();
  let activeElement = urlPath[0];
  if (activeElement.includes('/')) {
    const pathParts = activeElement.split('/');
    if (pathParts.length > 0) {
      activeElement = pathParts[0];
    }
  }

  LoggerClient.log('open', activeElement);

  const container = document.querySelector<HTMLDivElement>('.container')!;
  const leftPanel = new LeftPanel();
  let panelElements: any[] = [];
  try {
    panelElements = await MenuController.getMenu();
  } catch (error) {
    console.warn('Failed to fetch menu:', error);
  }
  const user = PortalAppContext.getCurrentUser();
  const editionEnabled = user.capabilities.includes('author');

  // remove the missing submaps
  let maps: any[] = [];
  if (!PortalAppContext.isLicenseExpired()) {
    try {
      maps = await GaligeoCloudService.getMaps();
    } catch (error) {
      console.warn('Failed to fetch maps:', error);
    }
  }

  if (maps.length > 0) {
    for (const element of panelElements) {
      if (element.maps) {
        element.maps = element.maps.filter((map: any) =>
          maps.some((m: any) => m.id === map.id),
        );
        // update the map urls
        for (const map of element.maps) {
          const correspondingMap = maps.find((m: any) => m.id === map.id);
          if (correspondingMap) {
            map.url = correspondingMap.url;
          } else {
            console.warn(`Map with id ${map.id} not found in the backend.`);
          }
        }
      }
    }
  }

  PortalAppContext.setMaps(maps);

  leftPanel.open = false;
  leftPanel.elements = panelElements;
  leftPanel.activeElement = activeElement.split('?')[0];

  let panelCloseTimer: any;
  leftPanel.addEventListener('click', (ev: any) => {
    const path = ev.composedPath();
    const isItemClick = path.some((el: any) => el.tagName === 'PANEL-ITEM');
    const isProfileCardClick = path.some((el: any) => el.classList?.contains('profile-card'));
    const isFooterClick = path.some((el: any) => el.tagName === 'PANEL-FOOTER');

    if (isItemClick) {
      return;
    }

    if (isProfileCardClick) {
      if (!leftPanel.open) {
        leftPanel.open = true;
      }
      return;
    }

    if (isFooterClick) {
      return;
    }

    leftPanel.open = !leftPanel.open
  });

  leftPanel.addEventListener('mouseenter', () => {
    clearTimeout(panelCloseTimer);
  });

  leftPanel.addEventListener('mouseleave', () => {
    panelCloseTimer = setTimeout(() => {
      leftPanel.open = false;
    }, 1500);
  });

  components.leftPanel = leftPanel;
  container.appendChild(leftPanel);

  const content = document.createElement('div');
  content.id = 'content';
  components.content = content;
  container.appendChild(content);

  const noParams = activeElement.split('?')[0];
  const selectedElement = panelElements.find((element: any) => {
    if (activeElement.includes('viewerId')) {
      if (element.id === noParams) {
        const map = element.maps.find(
          (map: any) => map.id === activeElement.split('=')[1],
        );
        if (map) {
          return map; // Return the map if it matches
        }
      }
    } else if (element.type) {
      if (element.type === noParams || element.id === noParams) {
        return true; // Return the element if it matches
      }
    }
    return false; // No match
  });

  let finalSelectedItem: any = null;
  const modules = panelElements.filter(e => e.url && e.type);
  const hasModule = modules.length;

  if (maps.length === 1 && hasModule === 0 && urlPath[0].length === 0) {
    finalSelectedItem = maps[0];
  } else if (maps.length === 0 && hasModule === 1 && !selectedElement) {
    finalSelectedItem = panelElements.find((element: any) => element.url && element.type);
    urlPath[0] = finalSelectedItem.type;
  }
  else {
    if (noParams === 'others') {
      const map = maps.find((map: any) => map.id === activeElement.split('=')[1]);
      map.categoryId = 'others';
      finalSelectedItem = map; // Return the map if 'others' is selected
    } else {
      finalSelectedItem = activeElement.includes('viewerId')
        ? (selectedElement?.maps?.find(
          (map: any) => map.id === activeElement.split('=')[1],
        ) ?? selectedElement)
        : selectedElement;
    }
  }

  const adminPanel = new AdminRightPanel();
  const rightPanel = new RightPanel();
  rightPanel.elements = panelElements;
  rightPanel.selectedItem = finalSelectedItem;
  rightPanel.route = urlPath[0];
  rightPanel.showSpinner = true;
  components.rightPanel = rightPanel;
  content.appendChild(rightPanel);

  console.log('Current User:', currentUser);
  console.log('Retail Token:', RESTUtils.getToken());
  console.log('Backend URL:', PortalAppContext.getBackendUrl());
  console.log('AppContext:', PortalAppContext.getContext());

  MapModalController.init();

  const licenseExpired = PortalAppContext.isLicenseExpired();

  if (
    !licenseExpired &&
    panelElements.filter(
      (element) =>
        element.id === 'others' || element.type || element.maps?.length > 0,
    ).length === 0
  ) {
    MapModalController.openMapModal({
      editionEnabled,
      categoriesEditable: true,
    });
  }

  subscribe(PUBSUB_TOPICS.MENU_CLICKED, (msg, data) => {
    console.log('Menu clicked:', data.element, msg);
    console.log('Event', data.event);
    if (licenseExpired) {
      return;
    }
    if (leftPanel.activeElement === data.element.id) {
      leftPanel.open = !leftPanel.open;
    }
    if (
      leftPanel.activeElement === data.element.id ||
      data.event.ctrlKey ||
      data.event.metaKey
    ) {
      if (
        (data.element.maps?.length > 1 && data.element.type == null) ||
        data.element.id === 'others'
      ) {
        // If the element has multiple maps or is 'others', open the map modal
        publish(PUBSUB_TOPICS.PLUS_BTN_CLICKED, { oldCat: leftPanel.activeElement, clickedElement: data.element });
        LoggerClient.log('map-modal', data.element.label);
        return;
      } else {
        return;
      }
    }

    if (data.element.id === 'others') {
      // open the map modal
      publish(PUBSUB_TOPICS.PLUS_BTN_CLICKED, { oldCat: leftPanel.activeElement, clickedElement: data.element });
    } else if (data.element.type == null) {
      if (data.element.maps?.length > 1) {
        // open the map modal
        publish(PUBSUB_TOPICS.PLUS_BTN_CLICKED, { oldCat: leftPanel.activeElement, clickedElement: data.element });
        LoggerClient.log('map-modal', data.element.label);
      } else if (data.element.maps?.length === 1) {
        // if there is only one map, show it directly
        rightPanel.selectedItem = data.element.maps[0];
        rightPanel.route = data.element.maps[0].categoryId;
        LoggerClient.log('menu-click', `${data.element.maps[0].label}`);

        if (content.contains(adminPanel)) {
          content.removeChild(adminPanel);
        }
        content.appendChild(rightPanel);
      }
    } else {
      rightPanel.selectedItem = data.element;
      rightPanel.route = data.element.type ?? data.element.id;
      LoggerClient.log('menu-click', data.element.label);

      if (content.contains(adminPanel)) {
        content.removeChild(adminPanel);
      }
      content.appendChild(rightPanel);
    }

    leftPanel.activeElement = data.element.id;
  });

  subscribe(PUBSUB_TOPICS.PLUS_BTN_CLICKED, (msg, data) => {
    console.log(msg);
    if (licenseExpired) {
      return;
    }
    if (data.clickedElement.id === 'others') {
      console.log('Map modal open event received:', data.clickedElement);
      MapModalController.openMapModal({
        editionEnabled,
        categoriesEditable: true,
        oldCategory: data.oldCat,
      });
      LoggerClient.log('others', data.clickedElement.id);
    } else {
      MapModalController.openMapModal({
        editionEnabled,
        categoriesEditable: false,
        category: data.clickedElement.id,
        label: data.clickedElement.label,
        oldCategory: data.oldCat,
      });
      LoggerClient.log('map-modal', data.clickedElement.id);
    }
  });

  subscribe(PUBSUB_TOPICS.SHOW_CONTENT, (msg, data) => {
    if (licenseExpired) {
      return;
    }
    if (data.newTab) {
      let url = `${window.location.origin}/`;
      url = RESTUtils.setUrlParameter(url, 'ggo_token', RESTUtils.getToken());
      url = `${url}#${data.map.categoryId ?? 'others'}?viewerId=${data.map.id}`;
      console.log('Opening in new tab:', url);
      leftPanel.activeElement = '';
      window.open(url, '_blank');
      return;
    }
    console.log('Show content:', data.map, msg);
    if (data.map.label) document.title = data.map.label;
    rightPanel.selectedItem = data.map;
    rightPanel.route = data.map.categoryId;
    leftPanel.activeElement = data.map.type ?? data.map.categoryId ?? '';
    if (content.contains(adminPanel)) {
      adminPanel.remove();
    }
    if (!content.contains(rightPanel)) {
      content.appendChild(rightPanel);
    }
    LoggerClient.log('open-map', data.map.label || data.map.id);
  });

  subscribe(PUBSUB_TOPICS.ADMIN_MODULE_SELECTED, (msg: any, module: any) => {
    rightPanel.remove();
    if (module === 'general') {
      adminPanel.route = 'admin/general';
      adminPanel.selectedItem = { label: 'General Settings' };
    } else {
      console.log(msg, module);
      adminPanel.selectedItem = module;
      adminPanel.route = module.type ?? module.id;
    }
    content.appendChild(adminPanel);
    leftPanel.activeElement = 'admin';
    LoggerClient.log('admin', module);
  });

  subscribe(PUBSUB_TOPICS.GO_TO_ADMIN, () => {
    if (content.contains(adminPanel)) {
      adminPanel.remove();
    }
    rightPanel.route = 'admin';
    rightPanel.selectedItem = { label: 'Administration' };
    content.appendChild(rightPanel);
    leftPanel.activeElement = 'admin';
    LoggerClient.log('go-to-admin-selection', 'admin');
  });

  subscribe(PUBSUB_TOPICS.REFRESH_LEFT_PANEL, async (msg, data) => {
    const panelElements = await MenuController.getMenu();
    console.log(data)

    const maps = await GaligeoCloudService.getMaps();
    for (const element of panelElements) {
      if (element.maps) {
        element.maps = element.maps.filter((map: any) =>
          maps.some((m: any) => m.id === map.id),
        );
      }
    }

    PortalAppContext.setMaps(maps);
    leftPanel.elements = panelElements;
    rightPanel.elements = panelElements;
    if (data) {
      leftPanel.activeElement = data.resetActiveElement;
    }
  });

  try {
    console.log('[portal] Calling syncSchema...');
    const response = await DeployService.syncSchema();
    console.log('[portal] syncSchema response:', response);
    console.log('[portal] syncSchema completed successfully');
  } catch (error: any) {
    console.error('Error syncing schema on portal load:', error);
    Message.alert('Error syncing schema on portal load: ' + (error?.error || String(error)));
  }
};

run();